import { useCallback, useMemo } from 'react';

import { useAsyncStorage } from '@sg/garnish';

import { CONFIG } from '../../launch-darkly.config';

import type { BaseFeatureFlags } from '../../launch-darkly.types';

// ─────────────────────────────────────────────────────────────────────────────

export const useAsyncStorageOverrides = <FeatureFlags extends BaseFeatureFlags>() => {
  //
  // ─── State ───────────────────────────────────────────────────────────

  const [overrides, setOverrides] = useAsyncStorage(CONFIG.FEATURE_FLAG_OVERRIDES_STORAGE_KEY);

  // ─── Derived Data ────────────────────────────────────────────────────

  const parsedOverrides = useMemo<FeatureFlags>(
    () => parseLocalStorageOverrides<FeatureFlags>(overrides),
    [overrides],
  );

  // ─── Helpers ─────────────────────────────────────────────────────────

  const setFeatureFlagOverrides = useCallback(
    (flags: Partial<FeatureFlags>) => {
      setOverrides(JSON.stringify(flags));
    },
    [setOverrides],
  );

  const assignFeatureFlagOverrides = useCallback(
    (flags: Partial<FeatureFlags>) => {
      setOverrides(JSON.stringify({ ...parsedOverrides, ...flags }));
    },
    [parsedOverrides, setOverrides],
  );

  const clearFeatureFlagOverrides = useCallback(() => {
    setOverrides(JSON.stringify({}));
  }, [setOverrides]);

  // ─────────────────────────────────────────────────────────────────────

  return useMemo(
    () => ({
      overrides: parsedOverrides,
      setFeatureFlagOverrides,
      assignFeatureFlagOverrides,
      clearFeatureFlagOverrides,
    }),
    [
      parsedOverrides,
      setFeatureFlagOverrides,
      assignFeatureFlagOverrides,
      clearFeatureFlagOverrides,
    ],
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function parseLocalStorageOverrides<FeatureFlags extends BaseFeatureFlags>(
  overrides: string | undefined,
): FeatureFlags {
  const emptyFlags = {} as unknown as FeatureFlags;

  return overrides ? JSON.parse(overrides) : emptyFlags;
}
